import { AgTechFormContext } from "@agtech/core/forms/AgTechFormContext";
import { AgTechFormSection } from "@agtech/web/components/Forms/Layout/AgTechFormSection";
import { SaleInvoiceDialogFormContext, SaleInvoiceDialogFormData } from "../InvoiceCreationDialog";
import AgTechFormBody from "@agtech/web/components/Forms/Layout/AgTechFormBody";
import ContactCreditDialog from "../../../../contacts/dialogs/credits/SaleContactCreditDialog";
import SaleInvoiceModifierDialog from "../../../../invoicemodifiers/dialogs/SaleInvoiceModifierDialog";
import { useAgTechQueryCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { InvoiceModifierDataGetAllInvoiceModifiersQuery } from "@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierDataQueries";
import { SaleContactDataGetContactDetailsQuery } from "@sales/data/operation/contacts/SaleContactDataQueries";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import InvoiceChangeInvoiceModifiersGrid from "../../../components/grids/InvoiceChangeInvoiceModifiersGrid";
import { buildGridEntitySelectionIndex, markGridSelectionEntityAsSelected } from "@agtech/web/components/Grids/AgTechGridEntities";
import InvoiceChangeCreditsGrid from "../../../components/grids/InvoiceChangeCreditsGrid";

const InvoiceInvoiceModifiersAndCreditsPage = (props: {
    formContext: AgTechFormContext<SaleInvoiceDialogFormData, SaleInvoiceDialogFormContext>,
}) => {
    const saleBidderContact = props.formContext.context.contactDetails.contact;
    const pageQuery = useInvoiceInvoiceModifiersAndCreditsPageQuery(props);

    return (
        <AgTechQueryLoadingSurface query={pageQuery}>
            {({ InvoiceModifiers, contactDetails }) => (
                <AgTechFormBody>
                    <AgTechFormSection
                        header={`Charges and discounts (${Object.keys(InvoiceModifiers).length})`}
                        classes='flexed'
                        bodyClasses='flexed py-3'
                        headerContent={() => (
                            <SaleInvoiceModifierDialog.Button
                                classes='button-thin button-link !pr-0'
                            />
                        )}
                    >
                        <InvoiceChangeInvoiceModifiersGrid
                            InvoiceModifierSelections={buildGridEntitySelectionIndex({
                                entities: Object.values(props.formContext.entity.appliedInvoiceModifiers),
                                isSelected: e => props.formContext.entity.appliedInvoiceModifiers[e.id] !== undefined
                            })}
                            onInvoiceModifierSelected={async InvoiceModifier => {
                                props.formContext.updateFormEntity(curr => {
                                    return {
                                        ...curr,
                                        appliedInvoiceModifiers: {
                                            ...curr.appliedInvoiceModifiers,
                                            [InvoiceModifier.id]: InvoiceModifier
                                        }
                                    }
                                });
                            }}
                            onInvoiceModifierDeselected={async InvoiceModifier => {
                                props.formContext.updateFormEntity(curr => {
                                    delete curr.appliedInvoiceModifiers[InvoiceModifier.id];

                                    return {
                                        ...curr,
                                        appliedInvoiceModifiers: {
                                            ...curr.appliedInvoiceModifiers
                                        }
                                    }
                                });
                            }}
                        />
                    </AgTechFormSection>
                    <AgTechFormSection
                        header={`Credits (${Object.keys(contactDetails.credits).length})`}
                        classes='flexed'
                        bodyClasses='flexed py-3'
                        headerContent={() => (
                            <ContactCreditDialog.Button
                                contactId={saleBidderContact.id}
                                classes='button-thin button-link !pr-0'
                            />
                        )}
                    >
                        <InvoiceChangeCreditsGrid
                            contactId={props.formContext.entity.contactId}
                            credits={buildGridEntitySelectionIndex({
                                entities: Object.values(props.formContext.entity.appliedCredits),
                                isSelected: e => props.formContext.entity.appliedCredits[e.id] !== undefined
                            })}
                            onCreditSelected={async credit => {
                                props.formContext.updateFormEntity(curr => {
                                    return {
                                        ...curr,
                                        appliedCredits: {
                                            ...curr.appliedCredits,
                                            [credit.id]: credit
                                        }
                                    }
                                });
                            }}
                            onCreditDeselected={async credit => {
                                props.formContext.updateFormEntity(curr => {
                                    delete curr.appliedCredits[credit.id];

                                    return {
                                        ...curr,
                                        appliedCredits: {
                                            ...curr.appliedCredits
                                        }
                                    }
                                });
                            }}
                        />
                    </AgTechFormSection>
                </AgTechFormBody>
            )}
        </AgTechQueryLoadingSurface>
    )
}

const useInvoiceInvoiceModifiersAndCreditsPageQuery = (props: { 
    formContext: AgTechFormContext<SaleInvoiceDialogFormData, SaleInvoiceDialogFormContext>
}) => {
    return useAgTechQueryCombination({
        firstQuery: {
            query: InvoiceModifierDataGetAllInvoiceModifiersQuery
        },
        secondQuery: {
            query: SaleContactDataGetContactDetailsQuery,
            queryContext: {
                contactId: props.formContext.entity.contactId
            }
        },
        projection: (InvoiceModifiers, contactDetails) => ({
            InvoiceModifiers,
            contactDetails
        })
    })
}

export default InvoiceInvoiceModifiersAndCreditsPage;